import React from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'
import { TaskTemplateStep } from '@core/entities/task/TaskTemplateStep'
import { useReopenTask } from '@core/react-query/features/tasks/task'
import { localizedStrings } from '@core/strings'
import {
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@common/material'
import { Flex } from '@fundamentals'
import { useNavigationContext } from '@common/hooks'
import { ModalDialogContainer } from '@common/modal'
import ClearIcon from '@mui/icons-material/Clear'
import { TaskAssignUserPicker, TaskStepPicker } from '@elements'
import { MAX_TASK_SUMMARY_CHARACTERS } from '@core/components/templateAnswersForm'

interface ComponentType {
  task: Task
}

type FormData = {
  step: TaskTemplateStep
  assigneeUser: TaskAssignableUser
  reason?: string
}

export const ReopenTaskModal: React.FC<ComponentType> = ({ task }) => {
  const { projectId } = useNavigationContext()
  const { hideModal } = useModal()
  const toast = useToast()
  const { control, watch, handleSubmit } = useForm<FormData>({
    defaultValues: {
      step: task.hasOnlyOneWorkStep()
        ? task.template?.currentVersion.steps?.taskSteps?.filter(
            (step) => step.type === 'WORK',
          )?.[0]
        : null,
      assigneeUser: TaskAssignableUser.new(task?.assignee),
      reason: '',
    },
  })

  const watchStep = watch('step')

  const reopenTaskMutation = useReopenTask()

  const onSubmit: SubmitHandler<FormData> = (data) => {
    reopenTaskMutation.mutate(
      {
        id: task.id,
        currentStepId: data.step.id,
        assigneeUserId: data.assigneeUser.id,
        reason: data.reason?.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.taskReopenedSuccessfully)
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.reopenTask}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection={'column'} mt={2}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name='step'
              rules={{ required: 'Please select a step' }}
              render={({ field, fieldState }) => (
                <TaskStepPicker
                  steps={task.template.currentVersion.steps.taskSteps?.filter(
                    (step) => step.type === 'WORK',
                  )}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              control={control}
              name='assigneeUser'
              rules={{ required: 'Please select an assignee' }}
              render={({ field, fieldState }) => (
                <TaskAssignUserPicker
                  projectId={projectId ?? task?.project?.id}
                  templateId={task.template.currentVersion.templateId}
                  stepId={watchStep?.id?.toString()}
                  value={field.value}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                />
              )}
            />

            <Controller
              name='reason'
              control={control}
              render={({ field: { onChange, ...field }, fieldState }) => (
                <TextField
                  label={localizedStrings.reason}
                  placeholder={localizedStrings.enterReason}
                  rows={3}
                  sx={{ mt: 2 }}
                  error={!!fieldState.error}
                  helperText={`${
                    field.value?.length || 0
                  }/${MAX_TASK_SUMMARY_CHARACTERS} ${
                    localizedStrings.characters
                  }`}
                  onChange={(e) =>
                    e.target.value.length <= MAX_TASK_SUMMARY_CHARACTERS &&
                    onChange(e)
                  }
                  slotProps={{
                    formHelperText: {
                      error: !!fieldState.error,
                      sx: { pt: 0.5, pb: 2 },
                    },
                  }}
                  fullWidth
                  multiline
                  {...field}
                />
              )}
            />

            <Flex gap={2} mt={4}>
              <Button variant='outlined' onClick={hideModal} fullWidth>
                {localizedStrings.cancel}
              </Button>
              <Button
                variant='contained'
                type='submit'
                fullWidth
                data-test='save-task'
                disabled={reopenTaskMutation.isLoading}
              >
                {localizedStrings.save}
              </Button>
            </Flex>
          </form>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
